import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { cloneDeep } from '@apollo/client/utilities';
import { useEmptyState } from '@facephi/inphinite-common';
import {
  ButtonIcon,
  OptionMenu,
  OptionMenuItem,
  PositionX,
  TData,
  TableSelectionLegacy,
  useTimezone,
  useToast,
} from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import { CellProps, Column } from 'react-table';
import { HeaderRules, ModalFace } from '.';
import { TableFaceImg } from './Styles';
import { useDelete, usePagination } from '../hooks';
import { MAX_ITEMS_PAGE } from '../state/constants';
import { DataFaceModal, FaceDto, FaceQueryDto } from '../state/model';
import {
  insertFace,
  removeFace,
  undoRemoveFace,
  updateFace,
} from '../state/mutations/faces';
import { searchFaces } from '../state/queries/faces';

export type FaceProps = {
  id: string;
  media_id: string;
  faceId: string;
  description: string;
  update: string;
  action: string;
};

export const RulesFace = () => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { t } = useTranslation();
  const { formatTimezone } = useTimezone();
  const [dateFilter, setDateFilter] = useState<Date | undefined>();
  const [createFace] = useMutation(insertFace);
  const [editableFace, setEditableFace] = useState<DataFaceModal | undefined>();
  const [editFace] = useMutation(updateFace);
  const { toastManager } = useToast();
  const [itemsDelete, setItemsDelete] = useState<string[]>([]);

  const getDateRange = () => {
    return dateFilter
      ? {
          _gte: dateFilter,
          _lt: new Date(
            new Date(dateFilter).setDate(dateFilter.getDate() + 1)
          ).toISOString(),
        }
      : {};
  };

  const columns: TData[] = useMemo(
    () => [
      {
        Header: t('Face'),
        accessor: 'face',
        maxWidth: 200,
        Cell: (props: CellProps<FaceProps>) => {
          return (
            <TableFaceImg
              id={props.row.original.faceId}
              alt={props.row.original.description}
            />
          );
        },
      },
      {
        Header: t('Update'),
        accessor: 'update',
        maxWidth: 130,
      },
      {
        Header: t('Description'),
        accessor: 'description',
      },
      {
        Header: t('Actions'),
        accessor: 'action',
        Cell: (props: CellProps<FaceProps>) => {
          return (
            <OptionMenu
              hidePanelOnClick
              renderItem={<ButtonIcon variant="text" iconName="ListPlus" />}
              positionX={PositionX.end}
              testId="option-menu-user"
            >
              <OptionMenuItem
                testId="button-edit-face"
                onClick={() => {
                  onEditHandle(props.row.original);
                }}
                iconName="Pencil"
              >
                {t('Edit')}
              </OptionMenuItem>
              <OptionMenuItem
                testId="button-delete-face"
                onClick={() => {
                  handleDelete([props.row.original.id]);
                }}
                iconName="Trash"
                color="error"
              >
                {t('Delete')}
              </OptionMenuItem>
            </OptionMenu>
          );
        },
        maxWidth: 150,
      },
    ],
    []
  );

  const variables = useMemo(
    () => ({
      limit: MAX_ITEMS_PAGE,
      offset: 0,
      updatedAt: getDateRange(),
    }),
    [MAX_ITEMS_PAGE]
  );

  const { data, loading, fetchMore, error, refetch } = useQuery<FaceQueryDto>(
    searchFaces,
    {
      notifyOnNetworkStatusChange: true,
      variables,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    }
  );

  const { handleDelete } = useDelete(
    removeFace,
    undoRemoveFace,
    'FaceDto',
    refetch
  );

  const facesData = useMemo(() => (data ? data.searchFaces.faces : []), [data]);
  const total = useMemo(() => (data ? data.searchFaces.count : 0), [data]);

  const { handlePagination, hasMore } = usePagination(
    facesData,
    loading,
    fetchMore,
    total
  );

  const tableData = useMemo(() => {
    return (
      data?.searchFaces.faces.map((face: FaceDto) => ({
        id: face.id,
        media_id: face.media_id,
        faceId: face.face.id,
        update: formatTimezone(face.updated_at),
        description: face.description,
      })) || []
    );
  }, [data]);

  const setItemsDeleteState = (items: FaceDto[]) => {
    setItemsDelete(items.map((item) => item.id));
  };

  const onEditHandle = (face: DataFaceModal) => {
    setShowModal(true);
    setEditableFace(face);
  };

  const onCreate = async (face: DataFaceModal) => {
    await createFace({
      variables: {
        face: face,
      },
      update: (cache, { data: { insertFace: newFace } }) => {
        if (newFace && data) {
          const queryData = cloneDeep(data);

          queryData.searchFaces.faces.unshift(newFace);
          queryData.searchFaces.count = queryData.searchFaces.count + 1;
          cache.writeQuery({
            query: searchFaces,
            variables: {
              limit: MAX_ITEMS_PAGE,
              offset: 0,
              updatedAt: getDateRange(),
            },
            data: queryData,
          });
        }
      },
      onCompleted: () => {
        toastManager({
          type: 'success',
          message: t('Face created successfully'),
          duration: 3000,
          testId: 'toast-create-face',
        });
      },
    });

    setShowModal(false);
  };

  const onEdit = async (face: DataFaceModal) => {
    await editFace({
      variables: {
        id: face.id,
        face: face,
      },
      onCompleted: () => {
        toastManager({
          type: 'success',
          message: t('Face updated successfully'),
          duration: 3000,
          testId: 'toast-update-face',
        });
      },
    });

    setShowModal(false);
  };

  const { emptyState } = useEmptyState({
    data: data?.searchFaces.faces || [],
    hasError: Boolean(error),
    isLoading: loading,
    isFilter: Boolean(tableData.length),
    onRetry: () => refetch(variables),
  });

  useEffect(() => {
    if (!showModal) {
      setEditableFace(undefined);
    }
  }, [showModal]);

  return (
    <>
      <HeaderRules
        onAddNew={() => setShowModal(true)}
        onChange={(date: Date) => setDateFilter(date)}
        itemsDelete={itemsDelete.length}
        deleteItemsSelected={() => handleDelete(itemsDelete)}
      />
      <TableSelectionLegacy
        columns={columns as Column<TData>[]}
        data={tableData}
        loading={loading}
        fetchMore={handlePagination}
        hasMore={hasMore}
        onSelectedRowsChange={setItemsDeleteState}
        emptyStateData={emptyState}
      />
      <ModalFace
        show={showModal}
        onSave={editableFace ? onEdit : onCreate}
        onChangeShow={(showModalFace) => setShowModal(showModalFace)}
        data={editableFace}
      />
    </>
  );
};
